import React, { useLayoutEffect, useRef, useState } from 'react';
import GraphGrid from '../graph_pages/GraphGrid';
import ZoomOverlay from '../terrain3d/ZoomOverlay';
import { buildChapters, chapterLabel, hasChapters, locateStep } from './chapters';
import { TerritoryChartGrid } from './TerritoryChart';
import './TerritoryIntroPage.css';

/**
 * LE PANNEAU D'ÉTAPE, isolé de la page qui le porte.
 *
 * POURQUOI IL VIT SEUL. Il est rendu à deux endroits : l'écran joueur
 * (TerritoryIntroPage) et le canevas d'édition du builder (StepCanvas), qui
 * en dessinait jusqu'ici une imitation — un rectangle au bon endroit, avec un
 * croquis dedans. Cette imitation devait redire, en constantes JavaScript, la
 * géométrie que la feuille de style décrit déjà : largeur du rail, hauteur du
 * bandeau, largeur du grand panneau. Les deux descriptions ont divergé (le
 * fantôme a longtemps annoncé un grand panneau de 980 px quand le CSS en
 * dessinait 760), et elles ne pouvaient plus converger depuis que le bandeau
 * et le grand panneau ont une hauteur libre : aucune constante ne dit la
 * hauteur d'un texte.
 *
 * Le builder monte donc le VRAI panneau, à l'échelle, dans un plateau aux
 * dimensions de l'écran projeté. Il n'y a plus une géométrie de référence et
 * une copie : il y a le panneau, et une réduction homothétique.
 *
 * Ce module ne contient donc que ce que les deux usages partagent — la forme
 * du panneau et son contenu. Ce qui appartient à la séance (navigation réelle,
 * sélecteur de disposition, réglages d'écran) reste dans TerritoryIntroPage,
 * et le builder passe ses propres leurres.
 */

// Écran de référence : le module est composé pour être projeté en 16:9. Toutes
// les mesures ci-dessous, et toute la feuille de style, sont exprimées dans ce
// repère — c'est lui que le canevas du builder met à l'échelle.
export const REFERENCE_WIDTH = 1920;
export const REFERENCE_HEIGHT = 1080;

// Encombrement des panneaux ancrés (doit rester synchronisé avec
// .territory-intro-panel--rail / --bottom dans la feuille de style) : sert à
// calculer la zone de carte masquée, que le viewer compense en décalant sa
// caméra. La colonne a une largeur fixe ; le bandeau, lui, n'a qu'une hauteur
// MAXIMALE — il épouse son contenu, et sa hauteur réelle est mesurée.
export const RAIL_WIDTH = 470;
export const BAND_MAX_HEIGHT = 340;
export const PANEL_MARGIN = 24;

// Types d'étape qui ont un bloc de contenu à afficher — "explore" (rien à
// montrer) et "points_of_interest" (le contenu, ce sont les points sur la
// carte elle-même) en sont exclus.
const KINDS_WITH_CONTENT = ['message', 'slide', 'graph', 'quiz', 'quiz_mouvant'];

/**
 * Le mode choisi par l'auteur (games_config) se traduit en deux réglages
 * indépendants : la TAILLE du panneau (colonne, bandeau bas ou grand panneau
 * centré) et son REPLI (masqué, carte plein cadre). Les séparer permet de
 * masquer le panneau depuis n'importe quelle taille — y compris en grand —
 * sans perdre la taille choisie quand on le rouvre.
 */
export function authorDefaults(panelMode) {
  if (panelMode === 'large') return { size: 'overlay', collapsed: false };
  if (panelMode === 'bottom') return { size: 'bottom', collapsed: false };
  if (panelMode === 'reduced') return { size: 'rail', collapsed: true };
  return { size: 'rail', collapsed: false };
}

/**
 * Hauteur réelle du bandeau bas, mesurée. Il épouse son contenu — une phrase
 * de narration ne réserve pas la même bande qu'un quizz en trois colonnes — et
 * c'est cette hauteur que le viewer retranche pour recentrer sa caméra sur la
 * portion de carte restée visible. Une constante forcerait à choisir entre un
 * bandeau toujours haut et un recentrage faux.
 *
 * Rend `[ref, height]` : la ref se pose sur le panneau, la hauteur est en
 * pixels du repère de référence (le panneau du builder vit dans un plateau
 * à cette échelle, la mesure n'a donc pas à être corrigée).
 */
export function useBandHeight(active) {
  const ref = useRef(null);
  const [height, setHeight] = useState(BAND_MAX_HEIGHT);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!active || !element) return undefined;
    const update = () => setHeight(element.offsetHeight);
    update();
    // Le contenu d'une étape à l'autre, une image qui finit de charger, la
    // fenêtre qu'on redimensionne : autant de changements de hauteur qu'aucun
    // rendu React ne signale.
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [active]);

  return [ref, height];
}

/**
 * Zone de carte que le panneau recouvre, en pixels de référence. Le viewer
 * recentre le terrain sur ce qui reste visible, au lieu de le laisser à moitié
 * dessous. En grand panneau, aucun décalage : le panneau est centré, et pour
 * voir la carte on le masque.
 */
export function panelContentInsets({ size, collapsed, bandHeight }) {
  if (collapsed) return null;
  if (size === 'rail') return { left: RAIL_WIDTH + PANEL_MARGIN * 2 };
  if (size === 'bottom') return { bottom: bandHeight + PANEL_MARGIN * 2 };
  return null;
}

/**
 * AVANCEMENT DANS LE CHEMINEMENT. Muet s'il n'y a qu'une étape.
 *
 * Ce qu'il montre : le SUJET en cours, et la place de l'écran courant dans ce
 * sujet. « Étape 5 sur 7 » disait un compte exact et sans usage — la salle ne
 * compte pas des écrans, elle suit un propos.
 *
 * La piste est faite de tirets, un par étape, groupés par chapitre : d'un coup
 * d'œil on voit combien il reste avant de changer de sujet, et combien de
 * sujets restent. Une barre pleine ne montrerait que la fraction parcourue,
 * qui est justement l'information dont personne ne fait rien.
 *
 * Repli documenté : tant qu'aucune étape ne porte de chapitre, on retombe sur
 * le décompte d'étapes. Un module non chapitré n'a pas à se faire passer pour
 * un module chapitré à un seul chapitre.
 */
export function StepProgress({ steps = [], stepIndex }) {
  const stepCount = steps.length;
  if (!(stepCount > 1)) return null;

  const chapters = buildChapters(steps);
  const located = locateStep(chapters, stepIndex);
  const chaptered = hasChapters(chapters) && located;

  const heading = chaptered
    ? chapterLabel(located.chapter, located.chapterIndex)
    : `Étape ${stepIndex + 1} sur ${stepCount}`;

  return (
    <div className="territory-intro-panel-heading">
      <div className="territory-intro-chapter-head">
        <span className="territory-intro-chapter-name" title={heading}>{heading}</span>
        {chaptered && chapters.length > 1 && (
          <span className="territory-intro-chapter-rank">
            {located.chapterIndex + 1}<span aria-hidden="true">/</span>{chapters.length}
          </span>
        )}
      </div>

      {/* La piste est décorative : tout ce qu'elle dit est déjà écrit en
          toutes lettres juste au-dessus, et le détail par étape n'a pas de
          sens hors du visuel. */}
      <div className="territory-intro-chaptertrack" aria-hidden="true">
        {chapters.map((chapter, chapterIndex) => (
          <span
            key={`${chapter.start}-${chapter.title || ''}`}
            className={
              'territory-intro-chaptergroup' +
              (located && chapterIndex === located.chapterIndex ? ' is-current' : '')
            }
            // Les tirets d'un chapitre occupent une part de la piste
            // proportionnelle au nombre d'étapes qu'il contient : un chapitre
            // de six écrans doit se voir plus long qu'un chapitre d'un seul.
            style={{ flexGrow: chapter.count }}
          >
            {Array.from({ length: chapter.count }, (unused, tick) => {
              const absolute = chapter.start + tick;
              const state = absolute < stepIndex ? ' is-done' : absolute === stepIndex ? ' is-current' : '';
              return <span key={absolute} className={`territory-intro-chaptertick${state}`} />;
            })}
          </span>
        ))}
      </div>

      {/* Ce que la piste ne peut pas dire à voix haute — et seulement ça : sans
          chapitres, le titre affiché EST déjà « Étape 2 sur 4 », le répéter ici
          le ferait lire deux fois. */}
      {chaptered && (
        <span className="territory-intro-visually-hidden">
          Étape {stepIndex + 1} sur {stepCount}, chapitre{' '}
          {located.chapterIndex + 1} sur {chapters.length} : {heading}
        </span>
      )}
    </div>
  );
}

/** Ligne de navigation. Sans gestionnaires (builder), les boutons sont inertes. */
export function StepNavRow({ isFirst = false, isLast = false, onPrevious, onNext }) {
  return (
    <div className="territory-intro-nav-row">
      {!isFirst && (
        <button type="button" className="territory-intro-prev" onClick={onPrevious}>
          Précédent
        </button>
      )}
      <button type="button" className="blue-button territory-intro-next" onClick={onNext}>
        {isLast ? 'Continuer' : 'Suivant'}
      </button>
    </div>
  );
}

/**
 * CARTES DE QUIZZ. Deux faces, et deux propos différents.
 *
 * FACE CACHÉE : un titre, et une image qui n'est là que pour donner envie —
 * elle illustre, elle ne dit rien. Elle est donc posée EN FOND, recadrée
 * (`cover`), sous un dégradé qui garde le titre lisible quelle que soit la
 * photo. La carte reste petite : plusieurs cartes se lisent d'un coup d'œil
 * côte à côte, et c'est la question qui doit tenir l'écran, pas les vignettes.
 *
 * FACE RÉVÉLÉE : le titre, l'explication, et une image qui, elle, PORTE le
 * propos (un schéma, une coupe, un graphique). Elle ne tient pas dans une
 * carte de cette taille — d'où le bouton d'agrandissement, qui la passe au
 * premier plan le temps du commentaire (cf. ZoomOverlay).
 *
 * L'état de retournement et l'agrandissement vivent ici : ils sont éphémères,
 * et remis à plat par le `key` posé sur StepContent à chaque changement
 * d'étape.
 */
function QuizCards({ quiz }) {
  const [revealedIndex, setRevealedIndex] = useState(null);
  const [zoomedIndex, setZoomedIndex] = useState(null);

  const cards = quiz?.cards || [];
  const zoomed = zoomedIndex === null ? null : cards[zoomedIndex];

  return (
    <div className="territory-intro-quiz">
      {quiz?.question && <p className="territory-intro-quiz-question">{quiz.question}</p>}

      <div className="territory-intro-quiz-cards">
        {cards.map((card, index) => {
          const isRevealed = revealedIndex === index;
          // L'image d'explication est facultative : une carte écrite avant que
          // ce champ existe n'a que son illustration, qui reste sur la face
          // cachée — la retourner ne doit pas la faire réapparaître en grand.
          const revealImage = card.reveal_image_url || '';

          if (!isRevealed) {
            return (
              <button
                key={index}
                type="button"
                className={`territory-intro-quiz-card${card.image_url ? ' has-image' : ''}`}
                onClick={() => setRevealedIndex(index)}
                style={card.image_url ? { backgroundImage: `url(${card.image_url})` } : undefined}
              >
                {/* Le voile est un élément et non un `background` de plus : il
                    doit passer AU-DESSUS de l'image de fond, et sous le texte. */}
                <span className="territory-intro-quiz-card-veil" aria-hidden="true" />
                <span className="territory-intro-quiz-card-front">
                  <span className="territory-intro-quiz-card-title">{card.title}</span>
                  <span className="territory-intro-quiz-card-cue">Cliquez pour découvrir</span>
                </span>
              </button>
            );
          }

          return (
            <div key={index} className="territory-intro-quiz-card revealed">
              <div className="territory-intro-quiz-card-backhead">
                <span className="territory-intro-quiz-card-title">{card.title}</span>
                <span className="territory-intro-quiz-card-tools">
                  {(revealImage || card.description) && (
                    <button
                      type="button"
                      className="territory-intro-quiz-card-tool"
                      onClick={() => setZoomedIndex(index)}
                      title="Agrandir cette carte"
                      aria-label="Agrandir cette carte"
                    >
                      ⤢
                    </button>
                  )}
                  <button
                    type="button"
                    className="territory-intro-quiz-card-tool"
                    onClick={() => setRevealedIndex(null)}
                    title="Retourner la carte"
                    aria-label="Retourner la carte"
                  >
                    ↩
                  </button>
                </span>
              </div>

              <div className="territory-intro-quiz-card-back">
                {revealImage && (
                  <button
                    type="button"
                    className="territory-intro-quiz-card-revealimage"
                    onClick={() => setZoomedIndex(index)}
                    title="Agrandir l'image"
                  >
                    <img src={revealImage} alt="" />
                  </button>
                )}
                {card.description && (
                  <p className="territory-intro-quiz-card-description">{card.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {zoomed && (
        <ZoomOverlay
          title={zoomed.title}
          imageUrl={zoomed.reveal_image_url || zoomed.image_url || ''}
          text={zoomed.description}
          onClose={() => setZoomedIndex(null)}
        />
      )}
    </div>
  );
}

/**
 * Contenu propre au type d'étape.
 */
export function StepContent({ step, gameStatus, territoryId = null, selection = null, panelSize = null }) {
  return (
    <>
      {KINDS_WITH_CONTENT.includes(step.kind) && (
        <div className="territory-intro-panel-content">
          {step.kind === 'message' && (
            <div className="territory-intro-message">
              {step.message?.media_url && (
                <img className="territory-intro-message-image" src={step.message.media_url} alt="" />
              )}
              <p className="territory-intro-message-text">{step.message?.text}</p>
            </div>
          )}

          {/* Diapo : une image, rien d'autre. Le cadre 16:9 est réservé même
              si l'image tarde ou manque, pour que le panneau ne saute pas de
              hauteur en cours de séance. */}
          {step.kind === 'slide' && (
            <div className="territory-intro-slide">
              {step.slide?.image_url ? (
                <img
                  className="territory-intro-slide-image"
                  src={step.slide.image_url}
                  alt={step.slide.alt || ''}
                />
              ) : (
                <p className="territory-intro-poi-hint">Aucune image n'a été déposée sur cette diapo.</p>
              )}
            </div>
          )}

          {/* Deux familles de graphiques cohabitent, et n'ont ni la même
              source ni le même propos. `charts` lit le DATAPACK du territoire
              (débits, climat, prélèvements, cultures) : c'est la forme
              courante, celle qu'on écrit dans le builder. `list_graph` lit le
              `gameStatus` d'une partie du serious game — les étapes écrites
              avant cette bascule le portent encore, et continuent de
              fonctionner. */}
          {step.kind === 'graph' && (
            <div className="territory-intro-graph">
              {step.charts || !step.list_graph ? (
                <TerritoryChartGrid
                  territoryId={territoryId}
                  charts={step.charts}
                  selection={selection}
                  panelSize={panelSize}
                />
              ) : (
                <GraphGrid listGraph={step.list_graph} gameStatus={gameStatus} />
              )}
            </div>
          )}

          {step.kind === 'quiz' && <QuizCards quiz={step.quiz} />}

          {step.kind === 'quiz_mouvant' && (
            <div className="territory-intro-quizmouvant">
              {step.quiz_mouvant?.image_url && (
                <img className="territory-intro-banner-image" src={step.quiz_mouvant.image_url} alt="" />
              )}
              {step.quiz_mouvant?.question && (
                <p className="territory-intro-quiz-question">{step.quiz_mouvant.question}</p>
              )}
              <div className="territory-intro-quizmouvant-options">
                {(step.quiz_mouvant?.options || []).map((option, index) => (
                  <div key={index} className="territory-intro-quizmouvant-option">
                    <span className="territory-intro-quizmouvant-number">{index + 1}</span>
                    <span className="territory-intro-quizmouvant-text">{option.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step.kind === 'points_of_interest' && (
        <p className="territory-intro-poi-hint">
          Cliquez sur les points de la carte pour en savoir plus.
        </p>
      )}

      {step.kind === 'explore' && (
        <p className="territory-intro-poi-hint">
          Parcourez le territoire librement, puis passez à la suite quand vous le souhaitez.
        </p>
      )}
    </>
  );
}

/**
 * Le panneau lui-même. `actions` (en-tête, à droite) et `nav` (en bas) sont
 * fournis par l'appelant : l'écran joueur y met des commandes réelles, le
 * canevas du builder des leurres non cliquables.
 */
export function StepPanel({
  step, size, gameStatus, steps = [], stepIndex, actions = null, nav = null, panelRef = null,
  territoryId = null, selection = null,
}) {
  return (
    <aside ref={panelRef} className={`territory-intro-panel territory-intro-panel--${size}`}>
      <div className="territory-intro-panel-inner">
        <header className="territory-intro-panel-head">
          {/* Le <span/> vide tient la place de l'avancement quand le module
              n'a qu'une étape : sans lui, l'en-tête étant en
              `space-between`, les commandes glisseraient à gauche. */}
          {steps.length > 1 ? <StepProgress steps={steps} stepIndex={stepIndex} /> : <span />}
          <div className="territory-intro-panel-actions">{actions}</div>
        </header>

        <div className="territory-intro-panel-body">
          <StepContent
            key={step.step_id || stepIndex}
            step={step}
            gameStatus={gameStatus}
            territoryId={territoryId}
            selection={selection}
            panelSize={size}
          />
        </div>

        {nav}
      </div>
    </aside>
  );
}

/**
 * Panneau masqué : une seule barre en bas à gauche, qui garde la navigation
 * accessible pour ne pas bloquer le cheminement. Elle reprend la même séquence
 * que l'en-tête d'une étape — navigation, puis commandes à droite — pour que
 * « Suivant » ne change pas de place quand le panneau disparaît.
 */
export function StepCollapsedBar({ steps = [], stepIndex, actions = null, nav = null }) {
  return (
    <div className="territory-intro-collapsedbar">
      <StepProgress steps={steps} stepIndex={stepIndex} />
      <div className="territory-intro-collapsedbar-row">
        {nav}
        {actions}
      </div>
    </div>
  );
}
